var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, isValidElement } from 'react';
import { useQuery } from 'react-query';
import { CtaGroup } from '../../CtaGroup';
import { useColorContext } from '../../ColorContext';
import { CtaCustomGroupsEditor } from '../../CtaCustomGroupsEditor';
import { CallToAction } from '../../CallToAction';
import { Input, Button, Icon, Dropdown } from 'semantic-ui-react';
import * as API from '../../../API';
import '../AnalyticsStage.scss';
export const TitleAndGroups = ({ step, analysis, knr = '', updateAnalysis = (_) => { } }) => {
    var _a, _b, _c, _d, _e;
    const colorContext = useColorContext();
    const emptyCtaGroups = () => {
        return {
            'single': [],
            'county': [],
            'kostra': [],
            'custom': []
        };
    };
    const groupIsAlreadyAdded = (items, key) => { var _a; return (_a = items === null || items === void 0 ? void 0 : items.some(item => item.key === key)) !== null && _a !== void 0 ? _a : false; };
    const [groupEditorProps, setGroupEditorProps] = useState(null);
    const [ctaGroups, setCtaGroups] = useState(emptyCtaGroups());
    const ctaCatPromptDict = {
        'single': 'Velg blant enkeltkommuner',
        'county': 'Velg blant fylker',
        'kostra': 'Velg blant kostragrupper',
        'custom': 'Velg blant egendefinerte grupper',
        'empty': 'Velg fra listen over først'
    };
    const { data: municipalityInfoData } = useQuery(`kb/kommuner/${knr}`, () => API.fetchMunicipalityInfo(knr), { enabled: !!knr });
    const { data: municipalityListData } = useQuery('kb/kommuner', () => API.fetchMunicipalityList());
    const { data: codeListData } = useQuery('kb/kodeliste', () => API.fetchCodeList());
    useEffect(() => {
        if (municipalityListData) {
            const municipalityList = Object.entries(municipalityListData !== null && municipalityListData !== void 0 ? municipalityListData : {}).map(([kommune_knr, item]) => {
                var _a;
                return {
                    key: kommune_knr,
                    title: item.kommune_navn,
                    description: 'Enkeltkommune',
                    text: `${item.kommune_knr} ${item.kommune_navn} kommune`,
                    value: item.kommune_knr,
                    disabled: groupIsAlreadyAdded((_a = analysis === null || analysis === void 0 ? void 0 : analysis.addedGroups) === null || _a === void 0 ? void 0 : _a.single, kommune_knr),
                    members: [kommune_knr],
                    aggregation: 'Enkeltkommuner',
                    expanded: false
                };
            });
            setCtaGroups((prev) => (Object.assign(Object.assign({}, prev), { single: municipalityList })));
        }
    }, [municipalityListData, analysis === null || analysis === void 0 ? void 0 : analysis.addedGroups]);
    useEffect(() => {
        setCtaGroups((prev) => {
            var _a;
            return (Object.assign(Object.assign({}, prev), { custom: [
                    ...((_a = analysis === null || analysis === void 0 ? void 0 : analysis.customGroups) !== null && _a !== void 0 ? _a : []).map(c => (Object.assign(Object.assign({}, c), { key: c.title, text: c.title, value: c.title, disabled: false, aggregation: 'Gjennomsnitt', expanded: false }))),
                    {
                        key: 'custom_new',
                        title: 'Opprett ny...',
                        description: 'Egendefinert kommunegruppe',
                        text: 'Opprett ny gruppe',
                        value: 'new',
                        disabled: false,
                        members: [],
                        aggregation: 'Enkeltkommuner',
                        expanded: false
                    }
                ] }));
        });
    }, [analysis.customGroups]);
    useEffect(() => {
        var _a, _b;
        if (codeListData) {
            const kostraGroupList = Object.entries((_a = codeListData.kostragrupper) !== null && _a !== void 0 ? _a : {}).map(([kostra_group, description]) => {
                var _a;
                const kostra_label = kostra_group.replace(/^EKG[0]?(\d{1,2})$/, "Kostragruppe $1");
                return {
                    key: kostra_group,
                    title: kostra_label,
                    description: 'Kostragruppe',
                    text: _jsxs("span", { className: "cta-kostra-description", children: [_jsxs("strong", { className: "kostra-code", children: [kostra_label, ":"] }), " ", description] }),
                    value: kostra_group,
                    disabled: groupIsAlreadyAdded((_a = analysis === null || analysis === void 0 ? void 0 : analysis.addedGroups) === null || _a === void 0 ? void 0 : _a.kostra, kostra_group),
                    members: codeListData.kostramedlemskap[kostra_group],
                    aggregation: 'Gjennomsnitt',
                    expanded: false
                };
            });
            const countyGroupList = Object.entries((_b = codeListData.fylker) !== null && _b !== void 0 ? _b : {}).map(([fylke_fnr, fylke_navn]) => {
                var _a;
                return {
                    key: fylke_fnr,
                    title: fylke_navn,
                    description: 'Enkeltkommune',
                    text: fylke_navn,
                    value: fylke_fnr,
                    disabled: groupIsAlreadyAdded((_a = analysis === null || analysis === void 0 ? void 0 : analysis.addedGroups) === null || _a === void 0 ? void 0 : _a.county, fylke_fnr),
                    members: Object.entries(codeListData.kommuner).filter(([k, _]) => k.startsWith(fylke_fnr)).map(([k, _]) => k),
                    aggregation: 'Gjennomsnitt',
                    expanded: false
                };
            });
            setCtaGroups((prev) => (Object.assign(Object.assign({}, prev), { county: countyGroupList, kostra: kostraGroupList })));
        }
    }, [codeListData, analysis === null || analysis === void 0 ? void 0 : analysis.addedGroups]);
    const validate = () => {
        var _a;
        return Object.keys((_a = analysis === null || analysis === void 0 ? void 0 : analysis.addedGroups) !== null && _a !== void 0 ? _a : {}).length > 0;
    };
    const onChangeCtaGroupCat = (e) => {
        updateAnalysis({ type: "groupCat", value: e.target.value });
    };
    const onChangeCtaGroupColorScheme = (newScheme) => {
        updateAnalysis({ type: "colorScheme", value: newScheme });
    };
    const openGroupEditor = (props) => setGroupEditorProps(prev => (Object.assign(Object.assign(Object.assign({}, prev), props), { open: true })));
    const closeGroupEditor = () => setGroupEditorProps(prev => (Object.assign(Object.assign({}, prev), { open: false })));
    const onCreateCustomGroup = () => {
        openGroupEditor({ onValidateAndClose: onCustomGroupsValidateAndClose });
    };
    const onCustomGroupsValidateAndClose = (validateFirst, group) => {
        var _a, _b;
        if (!validateFirst) {
            closeGroupEditor();
            return;
        }
        if (!group.title)
            return "Du må gi et navn til kommunegruppen";
        if (((_a = analysis.customGroups) !== null && _a !== void 0 ? _a : []).find(_g => _g.title === group.title))
            return `"${group.title}" er i bruk, velg et unikt navn`;
        if (!group.description)
            return "Du må gi en beskrivelse av kommunegruppen";
        if (!group.members || group.members.length === 0)
            return "Kommunegruppen er tom";
        closeGroupEditor();
        console.log(group);
        updateAnalysis({ type: "customGroups", value: [...((_b = analysis.customGroups) !== null && _b !== void 0 ? _b : []), group] });
    };
    const onAddCtaGroup = (data) => {
        var _a, _b, _c;
        if ((analysis === null || analysis === void 0 ? void 0 : analysis.groupCat) === 'custom' && data.value === 'new') {
            onCreateCustomGroup();
            return;
        }
        if (data.value && (analysis === null || analysis === void 0 ? void 0 : analysis.groupCat)) {
            const groups = ctaGroups[analysis === null || analysis === void 0 ? void 0 : analysis.groupCat];
            const g = groups.find(g => g.key === data.value);
            const current = (_b = (_a = analysis === null || analysis === void 0 ? void 0 : analysis.addedGroups) === null || _a === void 0 ? void 0 : _a[analysis === null || analysis === void 0 ? void 0 : analysis.groupCat]) !== null && _b !== void 0 ? _b : [];
            if (g && !current.some(item => item.key === g.key)) {
                updateAnalysis({ type: "addedGroups", value: Object.assign(Object.assign({}, ((_c = analysis === null || analysis === void 0 ? void 0 : analysis.addedGroups) !== null && _c !== void 0 ? _c : {})), { [analysis === null || analysis === void 0 ? void 0 : analysis.groupCat]: [...current, g] }) });
            }
        }
    };
    const onRemoveCtaGroup = (groupCat, groupKey) => {
        var _a, _b, _c;
        const current = (_a = analysis === null || analysis === void 0 ? void 0 : analysis.addedGroups) === null || _a === void 0 ? void 0 : _a[groupCat];
        if (!current) {
            return;
        }
        const remainingItems = current.filter(item => item.key !== groupKey);
        const _d = (_b = analysis === null || analysis === void 0 ? void 0 : analysis.addedGroups) !== null && _b !== void 0 ? _b : {}, _e = groupCat, _removed = _d[_e], otherGroups = __rest(_d, [typeof _e === "symbol" ? _e : _e + ""]);
        const updatedGroups = remainingItems.length > 0
            ? Object.assign(Object.assign({}, ((_c = analysis === null || analysis === void 0 ? void 0 : analysis.addedGroups) !== null && _c !== void 0 ? _c : {})), { [groupCat]: remainingItems }) : otherGroups;
        updateAnalysis({ type: "addedGroups", value: updatedGroups });
    };
    const onChangeCtaAggregation = (groupCat, parentId, aggregation) => {
        var _a;
        const group = (_a = analysis === null || analysis === void 0 ? void 0 : analysis.addedGroups) === null || _a === void 0 ? void 0 : _a[groupCat];
        if (group) {
            updateAnalysis({
                type: 'addedGroups',
                value: Object.assign(Object.assign({}, analysis === null || analysis === void 0 ? void 0 : analysis.addedGroups), { [groupCat]: group.map(item => item.key === parentId
                        ? Object.assign(Object.assign({}, item), { aggregation, expanded: aggregation === 'Enkeltkommuner' }) : item) }),
            });
        }
    };
    const onChangeCtaListExpansion = (groupCat, parentId, expanded) => {
        var _a, _b, _c;
        const currentGroups = (_a = analysis === null || analysis === void 0 ? void 0 : analysis.addedGroups) !== null && _a !== void 0 ? _a : {};
        const updatedGroups = Object.assign(Object.assign({}, currentGroups), { [groupCat]: [
                ...(_c = (_b = currentGroups[groupCat]) === null || _b === void 0 ? void 0 : _b.map((g) => {
                    if (g.key !== parentId) {
                        return g;
                    }
                    else {
                        return Object.assign(Object.assign({}, g), { expanded });
                    }
                })) !== null && _c !== void 0 ? _c : [],
            ] });
        updateAnalysis({ type: "addedGroups", value: updatedGroups });
    };
    const groupCat = analysis === null || analysis === void 0 ? void 0 : analysis.groupCat;
    const onEditCtaCustomGroup = (item) => {
        console.log("Edit");
    };
    const onDeleteCtaCustomGroup = (item) => {
        var _a;
        const a = analysis !== null && analysis !== void 0 ? analysis : {};
        console.log(item);
        console.log(a.customGroups);
        const customGroups = { type: 'customGroups', value: ((_a = a.customGroups) !== null && _a !== void 0 ? _a : []).filter(g => g.key !== item.key) };
        updateAnalysis(customGroups);
    };
    const toolIconsFor = (cat, item) => {
        switch (cat) {
            case 'custom':
                return _jsxs(_Fragment, { children: [_jsx(Icon, { title: "Slett", name: "trash", className: "right floated delete-button", onClick: (e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                onDeleteCtaCustomGroup(item);
                            } }), _jsx(Icon, { title: "Rediger", name: "pencil", className: "right floated edit-button", onClick: (e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                onEditCtaCustomGroup(item);
                            } })] });
            default:
                return _jsx(_Fragment, {});
        }
    };
    const groupOptions = groupCat
        ? ctaGroups[groupCat].map(item => {
            var _a;
            return ({
                key: item.key,
                text: item.text,
                value: item.value,
                icon: toolIconsFor(item.value !== 'new' ? groupCat : null, item),
                disabled: groupIsAlreadyAdded((_a = analysis === null || analysis === void 0 ? void 0 : analysis.addedGroups) === null || _a === void 0 ? void 0 : _a[groupCat], item.key),
            });
        })
        : [];
    const onNext = (e) => {
    };
    return (_jsx(_Fragment, { children: _jsxs(CallToAction, { step: step, vertline: true, clickableLabels: true, onInputValidate: validate, children: [_jsx(CtaCustomGroupsEditor, Object.assign({}, groupEditorProps)), _jsxs(CallToAction.Main, { children: [_jsx("div", { className: "cta-fieldset", children: _jsx(Input, { fluid: true, value: (_a = analysis === null || analysis === void 0 ? void 0 : analysis.title) !== null && _a !== void 0 ? _a : '', onChange: (e) => { updateAnalysis({ type: "title", value: e.target.value }); } }) }), _jsx(CallToAction.Title, { children: "Velg kommuner eller kommunegrupper" }), _jsx(CallToAction.Prompt, { children: "Begynn med \u00E5 velge \u00E9n eller flere kommuner eller grupper av kommuner som du vil unders\u00F8ke eller sammenlikne." }), _jsxs("section", { children: [_jsxs("div", { className: "cta-fieldset", children: [_jsx("h2", { className: "cta-title", children: "Legg til eller fjern:" }), _jsxs("ul", { className: "cta-group-list", children: [_jsxs("li", { children: [_jsx("input", { id: "cta-single", type: "radio", name: "cta-group", value: "single", checked: (analysis === null || analysis === void 0 ? void 0 : analysis.groupCat) === 'single', onChange: onChangeCtaGroupCat }), _jsx("label", { children: "Enkeltkommune" })] }), _jsxs("li", { children: [_jsx("input", { id: "cta-county", type: "radio", name: "cta-group", value: "county", checked: (analysis === null || analysis === void 0 ? void 0 : analysis.groupCat) === 'county', onChange: onChangeCtaGroupCat }), _jsx("label", { children: "Fylke" })] }), _jsxs("li", { children: [_jsx("input", { id: "cta-kostra", type: "radio", name: "cta-group", value: "kostra", checked: (analysis === null || analysis === void 0 ? void 0 : analysis.groupCat) === 'kostra', onChange: onChangeCtaGroupCat }), _jsx("label", { children: "Kostragruppe" })] }), _jsxs("li", { children: [_jsx("input", { id: "cta-custom", type: "radio", name: "cta-group", value: "custom", checked: (analysis === null || analysis === void 0 ? void 0 : analysis.groupCat) === 'custom', onChange: onChangeCtaGroupCat }), _jsx("label", { children: "Egendefinert gruppe" })] })] })] }), _jsx("div", { className: "cta-fieldset group-select", children: _jsx(Dropdown, { disabled: (analysis === null || analysis === void 0 ? void 0 : analysis.groupCat) === null, search: (analysis === null || analysis === void 0 ? void 0 : analysis.groupCat) ? !isValidElement((_c = (_b = ctaGroups[analysis.groupCat]) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.text) : true, selection: true, fluid: true, placeholder: ctaCatPromptDict[(_d = analysis === null || analysis === void 0 ? void 0 : analysis.groupCat) !== null && _d !== void 0 ? _d : 'empty'], noResultsMessage: 'Ingen resultater', options: groupOptions, value: '', onChange: (_, data) => onAddCtaGroup(data) }) }), _jsx(CtaGroup, { groups: (_e = analysis === null || analysis === void 0 ? void 0 : analysis.addedGroups) !== null && _e !== void 0 ? _e : {}, onRemove: onRemoveCtaGroup, onChangeColorScheme: onChangeCtaGroupColorScheme, colorScheme: analysis === null || analysis === void 0 ? void 0 : analysis.colorScheme, onChangeAggregation: onChangeCtaAggregation, onChangeListExpansion: onChangeCtaListExpansion })] }), _jsxs("section", { className: "cta-actions", children: [_jsx("div", { className: "prev-button", children: _jsx(Button, { fluid: true, children: "Forrige" }) }), _jsx("div", { className: "next-button", children: _jsx(Button, { onClick: onNext, fluid: true, children: "Neste" }) })] })] }), _jsx(CallToAction.Aside, {})] }) }));
};
